import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchLibraryDetail } from "../api/LibraryDetail";
import ErrorPage from "../not_found/NotFound";
import Loader from "../ui/Loader";
import { useTranslation } from "react-i18next";

const LibraryDetail = () => {
    const { t } = useTranslation("library");
    const { slug } = useParams();
    const [libraryDetail, setLibraryDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const getLibraryDetail = async () => {
            try {
                const data = await fetchLibraryDetail(slug);
                if (data.data && data.data.length > 0) {
                    setLibraryDetail(data.data[0]);
                } else {
                    setError(<ErrorPage
                        title={t("library_not_found")}
                        description={t("library_error_message")}
                    />);
                }
            } catch (err) {
                setError(<ErrorPage
                    title={t("library_not_found")}
                    description={t("library_error_message")}
                />);
            } finally {
                setLoading(false);
            }
        };

        getLibraryDetail();
    }, [slug]);

    if (loading) return <Loader />;
    if (error) return error;
    if (!libraryDetail) return <div>No data available</div>;

    const getFileNameFromUrl = (url) => {
        const parts = url.split(/[?#]/)[0].split("/");
        return parts[parts.length - 1];
    };

    return (
        <div className="relative">
            {/* Hero Section */}
            <div className="relative h-[384px] md:h-[384px] lg:h-[350px]">
                <img
                    src="/assets/servicesbg.svg"
                    className="w-full h-full object-cover"
                    alt="background animation"
                />
                <div className="absolute inset-0 bg-[#00000099]"></div>
                <img
                    src="/assets/ellipse.svg"
                    className="absolute bottom-64 left-1/2 transform -translate-x-1/2 translate-y-1/2"
                    alt="ellipse decoration"
                />
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-2 md:px-4">
                    <div className="title-small text-[#05897E] py-2 px-4 rounded-full mb-4 border border-[#FFFFFF33]">
                        {t("library_header")}
                    </div>
                    <h1 className="web-heading-main mb-4">
                        {libraryDetail.company_name}
                    </h1>
                    <p className="text-color body-large-30 mb-6 md:text-xl max-w-5xl">
                        {libraryDetail.description}
                    </p>
                </div>
            </div>

            {/* Content Section */}
            <div className="relative text-white px-4 py-8 max-w-7xl mx-auto mb-10">
                <div className="flex flex-col md:flex-row items-center justify-center">
                    <div className="w-full space-y-6">
                        {/* Dynamic Content Sections */}
                        {libraryDetail.content.map((section, index) => {
                            return Object.keys(section).map((key) => {
                                const match = key.match(/^heading(\d+)$/);
                                if (match) {
                                    const headingKey = key;
                                    const descriptionKey = `description${match[1]}`;
                                    const featuresKey = `features${match[1]}`;

                                    return (
                                        <div key={`${index}-${headingKey}`}>
                                            <h2 className="web-heading-main-4 text-color-6 tracking-tight mb-4">
                                                {section[headingKey]}
                                            </h2>
                                            {section[descriptionKey] && (
                                                <ul className="list-disc pl-6 body-large-8 text-color-7 tracking-[1px]">
                                                    {section[descriptionKey].map((desc, i) => (
                                                        <li key={i}>{desc}</li>
                                                    ))}
                                                </ul>
                                            )}
                                            {section[featuresKey] && (
                                                <ul className="list-disc pl-6 body-large-8 text-color-7 tracking-[1px] mt-4">
                                                    {section[featuresKey].map((feature, i) => (
                                                        <li key={i}>{feature}</li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    );
                                }
                                return null;
                            });
                        })}

                        {/* Uploaded Documents Section */}
                        {libraryDetail.files && libraryDetail.files.length > 0 && (
                            <div className="relative">
                                <h2 className="web-heading-main-4 text-color-6 tracking-tight mb-4">
                                    Uploaded Documents
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {libraryDetail.files.map((file, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center justify-between p-4 bg-[#FFFFFF33] rounded-md"
                                        >
                                            <div className="flex items-center space-x-3">
                                                <img
                                                    src="/assets/pdf-icon.svg"
                                                    alt="PDF icon"
                                                    className="w-6 h-6"
                                                />
                                                <div className="flex flex-col">
                                                    <span className="font-medium">
                                                        {getFileNameFromUrl(file)}
                                                    </span>
                                                    <span className="text-sm text-gray-400">
                                                        {libraryDetail.created_at}
                                                    </span>
                                                </div>
                                            </div>
                                            <a
                                                href={file}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <img
                                                    src="/assets/downloadbutton.svg"
                                                    alt="Download button"
                                                    className="w-6 h-6"
                                                />
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LibraryDetail;